import '@/global.css'
import { ReactNode } from "react";
import { cn } from '@/lib/utils';
import { Separator } from "../ui/separator";
import GamificationHeader from '../gamification/GamificationHeader';
import HeaderControls from "../header-controls";
import { useAuth } from "@/contexts/AuthContext";
import { UserInfoResponseRole } from "@/services/generatedAPIs.schemas";
import { useTranslation } from "react-i18next";

/**
 * 全站统一顶栏（按 Figma 隨心練 设计稿）：
 * - 大标题置于内容区左上（取代旧的小号灰字标题栏 + 分隔线）
 * - 右上角竖向集群：控制行（主题 / 規則 / 排行榜 / 语言 / 登出）在上,
 *   下方为 GamificationHeader（学生端 MPL/KP/GS 白色数据卡）。
 *   排行榜按钮已并入控制行（HeaderControls 内 GamificationLeaderboard）。
 * - className 透传到根 div: 让需要「整页不滚 / 局部滚动」的页面在外面套
 *   md:h-full md:overflow-hidden 锁高,内部 children 自己用 flex-1 + min-h-0
 *   + overflow-y-auto 处理局部滚动(如 PracticeEditPage 灰板)。默认不传时
 *   维持 min-h-full,允许整体 SidebarInset 滚动(其他 24 个页面行为不变)。
 */
const ContentLayout = ({ title, children, backgroundColor, className }: { title: string, children: ReactNode, backgroundColor?: string, className?: string }) => {

    const isStudent = useAuth().getCurrentUser()?.role === UserInfoResponseRole.STUDENT;
    // 仅用于 1920 档标题字号分档(判法同 header-controls.tsx)；须在下面早返回之前调用，否则非学生分支少一个 hook
    const { i18n } = useTranslation();
    const isEN = (i18n.language || '').startsWith('en');

    // 非学生（教师等）：还原 github/dev 顶栏（小号灰标题 + 分隔线），并隐藏学生专属的主题/規則/排行榜，语言走简版。
    if (!isStudent) {
        return (
            <div className={cn('min-h-full', backgroundColor ? '' : 'bg-background', className)} style={backgroundColor ? { backgroundColor } : {}}>
                <div className="flex min-w-0 items-center gap-4 bg-background p-4">
                    <h1 className="ml-3 min-w-0 flex-1 truncate text-xl font-bold text-muted-foreground">{title}</h1>
                    <div className="flex min-w-0 flex-1 items-center justify-end gap-2">
                        <div className="min-w-0 flex-1">
                            <GamificationHeader />
                        </div>
                        <HeaderControls legacy />
                    </div>
                </div>
                <Separator />
                {children}
            </div>
        )
    }

    return (
        <div className={cn('flex min-h-full flex-col', backgroundColor ? '' : 'bg-background', className)} style={backgroundColor ? { backgroundColor } : {}}>

            <div className="flex shrink-0 flex-col gap-4 px-6 pt-6 lg:px-10 lg:pt-8 sm:flex-row sm:items-start sm:justify-between">
                {/* 内容区大标题：Figma 64px 仅用于 1920 冻结稿，md–1919 逐级缩小避免与积分栏挤占 */}
                {/* EN-03：英文 1920 档改 52px(可用宽 701.4，64px 下「Mathematics Homework」807px 必折行，52px 656px 回一行)；两条都带 ! 故写成互斥分支，超长科目名仍由 line-clamp-2 兜住 */}
                <h1
                    title={title}
                    className={cn(
                        "order-2 min-w-0 line-clamp-2 break-words text-2xl font-bold leading-tight text-foreground sm:order-1 sm:self-end sm:text-3xl md:text-[32px] lg:text-4xl xl:text-[40px] 2xl:text-5xl",
                        isEN ? "min-[1920px]:text-[52px]!" : "min-[1920px]:text-[64px]!",
                    )}
                >
                    {title}
                </h1>

                {/* 右上集群：控制行 + 数据卡 */}
                {/* 控制行↔数据卡竖向间距:1920 冻 35px,md–1919 收窄一档 */}
                <div className="order-1 flex shrink-0 flex-col items-end gap-4 lg:gap-6 min-[1920px]:gap-[35px]! sm:order-2">
                    <div className="flex flex-wrap items-center justify-end gap-2">
                        <HeaderControls />
                    </div>
                    <div className="flex min-w-0 items-center justify-end">
                        <GamificationHeader />
                    </div>
                </div>
            </div>

            {children}
        </div>
    )
}

export default ContentLayout;
